import { useEffect, useMemo, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useSearchParams } from 'react-router-dom'
import { Banner, Breadcrumbs, Loader, LinkButton } from '@cloudflare/kumo'
import { ArrowLeftIcon, HouseIcon } from '@phosphor-icons/react'
import type { GuestInvitationCreateResult, RoomBookingDto } from '@hub-wifi/shared'
import { ApiError, apiRequest } from '../api/client'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import { GuestInvitationForm } from '../components/guests/GuestInvitationForm'
import { useGuestData } from '../components/guests/useGuestData'
import { useNow } from '../components/guests/useNow'
import '../components/guests/guests.css'

/** `/my/guests/new`: just the invitation form (`?roomBookingId=` links a booking, `?guest=` pre-fills). */
export function NewGuestInvitationPage() {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth0()
  const [searchParams] = useSearchParams()
  const roomBookingId = searchParams.get('roomBookingId') ?? undefined
  // "Invite again" chips link here with ?guest=<email>. Only matched against
  // the caller's own recent guests, never used as raw input.
  const prefillEmail = searchParams.get('guest')
  const data = useGuestData({ invitations: false })
  const now = useNow()

  // A booking opened by id may not be in the upcoming list (or may not be the
  // caller's): fetch it, and block sending if that fails.
  const listed = roomBookingId ? data.bookings.find((b) => b.id === roomBookingId) : undefined
  const [fetched, setFetched] = useState<RoomBookingDto | null>(null)
  const [bookingError, setBookingError] = useState<string | null>(null)
  useEffect(() => {
    if (!roomBookingId || data.loading || listed) return
    let cancelled = false
    void (async () => {
      try {
        const token = await getAccessTokenSilently()
        const dto = await apiRequest<RoomBookingDto>(`/api/my/bookings/${roomBookingId}`, {
          token,
        })
        if (cancelled) return
        // An old link can point at a cancelled or finished booking; the API
        // only checks ownership, so don't offer "With my booking" for it.
        if (dto.status !== 'confirmed' || new Date(dto.endAt).getTime() <= Date.now()) {
          setBookingError(t('guestInvitations.errors.BOOKING_UNAVAILABLE'))
          return
        }
        setFetched(dto)
      } catch (err) {
        if (cancelled) return
        setBookingError(
          err instanceof ApiError
            ? ((err.body as { message?: string } | undefined)?.message ?? err.message)
            : t('common.error'),
        )
      }
    })()
    return () => {
      cancelled = true
    }
  }, [roomBookingId, data.loading, listed, getAccessTokenSilently, t])
  const bookingLoading = Boolean(roomBookingId) && !listed && !fetched && !bookingError

  // Every upcoming booking can be linked (soonest first), plus the
  // `?roomBookingId=` one when it isn't in the list. "With my booking"
  // defaults to the linked booking, else the soonest; the user can pick
  // another from the list.
  const choices = useMemo(() => {
    const upcoming = data.bookings
      .filter((b) => b.status === 'confirmed' && new Date(b.endAt).getTime() > now.getTime())
      .sort((a, b) => a.startAt.localeCompare(b.startAt))
    const linked = roomBookingId ? (listed ?? fetched) : null
    return linked && !upcoming.some((b) => b.id === linked.id) ? [linked, ...upcoming] : upcoming
  }, [data.bookings, roomBookingId, listed, fetched, now])
  const [pickedBookingId, setPickedBookingId] = useState<string | null>(null)
  const booking = useMemo(
    () =>
      choices.find((b) => b.id === pickedBookingId) ??
      // A `?roomBookingId=` that failed to load links nothing (sending is
      // blocked and the banner says why) rather than silently another booking.
      (roomBookingId ? (listed ?? fetched ?? null) : (choices[0] ?? null)),
    [choices, pickedBookingId, roomBookingId, listed, fetched],
  )

  const initialGuest = useMemo(
    () =>
      prefillEmail
        ? (data.recent.find((g) => g.guestEmail.toLowerCase() === prefillEmail.toLowerCase()) ??
          null)
        : null,
    [prefillEmail, data.recent],
  )

  function onCreated(dto: GuestInvitationCreateResult) {
    if (!dto.guestEmail) return
    const email = dto.guestEmail.toLowerCase()
    data.setRecent([
      {
        guestName: dto.guestName,
        guestEmail: dto.guestEmail,
        guestPhone: dto.guestPhone,
        lastInvitedAt: dto.createdAt,
      },
      ...data.recent.filter((g) => g.guestEmail.toLowerCase() !== email),
    ])
  }

  const loading = data.loading || bookingLoading

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Link href="/my/guests">{t('guestInvitations.title')}</Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('guestInvitations.form.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('guestInvitations.form.title')}
      />
      <div style={{ marginBottom: 16 }}>
        <LinkButton href="/my/guests" variant="ghost" icon={<ArrowLeftIcon size={16} />}>
          {t('guestInvitations.title')}
        </LinkButton>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && data.error && (
        <Banner variant="error">
          {t('common.errorPrefix')}{' '}
          {data.error === 'GENERIC_ERROR' ? t('common.error') : data.error}
        </Banner>
      )}

      {!loading && !data.error && (
        <GuestInvitationForm
          timeZone={data.timeZone}
          hours={data.hours}
          booking={booking}
          bookingChoices={choices}
          onBookingChange={setPickedBookingId}
          bookingError={bookingError}
          recentGuests={data.recent}
          initialGuest={initialGuest}
          onCreated={onCreated}
        />
      )}
    </div>
  )
}
